'use client';

import { CandleResolution, MarketType } from '@velocity-exchange/sdk';
import {
	Candle,
	ENUM_UTILS,
	MarketId,
	ONE_DAY_MS,
	Serializer,
	UISerializableCandle,
	UISerializableOrderActionRecord,
} from '@velocity-exchange/common';
import { JsonObject } from 'cerializr';
import dayjs from 'dayjs';
import { usePathname } from 'next/navigation';
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { dlog } from '../../dev';
import useFillEventsBuffer from '../../hooks/velocityEvents/useFillEventsBuffer';
import useVelocityStore from '../../stores/VelocityStore/useVelocityStore';
import { getLiveOracleData } from 'src/stores/marketLiveState';
import {
	getPerpMarketSymbol,
	getSpotMarketSymbol,
} from 'src/utils/DataApiClient';
import StatsApiClient from 'src/utils/StatsApiClient';
import { notify } from '../../utils/notifications';
import { UICandleClient } from './UICandleClient';
import {
	ONE_MINUTE_MS,
	ONE_HOUR_MS,
	ZERO_PRICE_BIGNUM,
} from 'src/constants/math';
import useAppEventEmitter from 'src/hooks/useAppEventEmitter';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import { needsDataRequirement } from 'src/utils/routeDataRequirements';
import type { ResolutionString } from 'public/charting_library/charting_library';

// the key is derived from the local storage key used by TradingView - chart_last_selected_interval
type CandlesResolutionFromLocalStorage =
	| '1'
	| '3'
	| '5'
	| '15'
	| '30'
	| '60'
	| '240'
	| '360'
	| '480'
	| '1D'
	| '3D'
	| '1W'
	| '1M';

// this config map is based on TradingView's network requests params

type ResolutionFetchDetailsMap = {
	[K in CandlesResolutionFromLocalStorage]: {
		resolution: CandleResolution;
		countback: number;
	};
};

const STANDARD_COUNTBACK = 340; // usually its 320, but we add a buffer of 20 candles; otherwise the chart will make a second call if the countback is less than required

const RESOLUTION_FETCH_DETAILS_MAP: ResolutionFetchDetailsMap = {
	'1': {
		resolution: '1',
		countback: STANDARD_COUNTBACK,
	},
	'3': { resolution: '1', countback: STANDARD_COUNTBACK * 3 },
	'5': {
		resolution: '5',
		countback: STANDARD_COUNTBACK,
	},
	'15': {
		resolution: '15',
		countback: STANDARD_COUNTBACK,
	},
	'30': {
		resolution: '15',
		countback: STANDARD_COUNTBACK * 2,
	},
	'60': {
		resolution: '60',
		countback: STANDARD_COUNTBACK,
	},
	'240': {
		resolution: '240',
		countback: STANDARD_COUNTBACK,
	},
	'360': {
		resolution: '60',
		countback: STANDARD_COUNTBACK * 6,
	},
	'480': {
		resolution: '240',
		countback: STANDARD_COUNTBACK * 2,
	},
	'1D': {
		resolution: 'D',
		countback: STANDARD_COUNTBACK,
	},
	'3D': {
		resolution: 'D',
		countback: STANDARD_COUNTBACK * 3,
	},
	'1W': {
		resolution: 'D',
		countback: STANDARD_COUNTBACK * 7,
	},
	'1M': {
		resolution: 'D',
		countback: STANDARD_COUNTBACK * 31,
	},
};

export const CandlesContext = React.createContext<UICandleClient | undefined>(
	undefined
);

const candleHistoryFetcher = async (
	resolution: CandleResolution,
	marketId: MarketId,
	to: number, // ms
	from?: number, // ms
	countBack?: number,
	allowedRetryAttempts = 1
) => {
	try {
		// Get market symbol from marketId
		const isPerp = ENUM_UTILS.match(marketId.marketType, MarketType.PERP);
		const symbol = isPerp
			? getPerpMarketSymbol(marketId.marketIndex)
			: getSpotMarketSymbol(marketId.marketIndex);

		if (!symbol) {
			console.error(`Could not find symbol for market ${marketId.key}`);
			return { candles: [], lastTradeSeen: undefined };
		}

		// Convert resolution to Data API format
		const resolutionMap: Record<
			CandleResolution,
			'1' | '5' | '15' | '60' | '240' | 'D' | 'W' | 'M'
		> = {
			'1': '1',
			'5': '5',
			'15': '15',
			'60': '60',
			'240': '240',
			D: 'D',
			W: 'W',
			M: 'M',
		};

		const getHistory = () =>
			StatsApiClient.fetchMarketCandles({
				symbol,
				resolution: resolutionMap[resolution],
				limit: countBack ?? 340,
				// these may seem reversed, but the data api params are this way
				// we don't want to break it for users so it's staying as is for now
				startTs: to ? Math.floor(to / 1000) : undefined,
				endTs: from ? Math.floor(from / 1000) : undefined,
			});

		let historicalData = await getHistory();

		let hadToRetry = false;

		// Retry on failures
		if (!historicalData.success) {
			hadToRetry = true;

			let retryCount = 0;

			while (retryCount < allowedRetryAttempts) {
				dlog(
					`candle_robustness`,
					`candle_fetch_retry_attempt :: ${retryCount}`
				);

				historicalData = await getHistory();

				if (historicalData.success) {
					break;
				}

				retryCount++;
			}
		}

		if (!historicalData.success) {
			if (historicalData.status === 429) {
				notify({
					id: 'candles_too_many_requests',
					type: 'warning',
					message: 'Too many requests',
				});
			}

			console.error(`Failed to get historical bars in tv chart data feed`);

			return {
				candles: [],
				lastTradeSeen: undefined,
			};
		}

		if (historicalData.success && hadToRetry) {
			dlog(`candle_robustness`, `fetched_good_candles_after_retry_attempts`);
		}

		// Convert Data API response to UISerializableCandle format
		const candles: UISerializableCandle[] = (historicalData.records ?? [])
			.map((record) =>
				Serializer.Deserialize.UICandle(record as unknown as JsonObject)
			)
			.filter((candle): candle is UISerializableCandle => candle !== null);

		return {
			candles,
			lastTradeSeen: undefined as UISerializableOrderActionRecord | undefined,
			beyondMaxLookback: false,
		};
	} catch (e) {
		console.error(e);
		throw e;
	}
};

/**
 * This utility function is used to calculate the fromTs and toTs for the initial fetch of candles.
 * It closely mimics the logic used by TradingView.
 */
const getToAndFromTs = (
	candlesResolutionFromLocalStorage: CandlesResolutionFromLocalStorage
) => {
	const { resolution, countback } =
		RESOLUTION_FETCH_DETAILS_MAP[candlesResolutionFromLocalStorage];

	const currentExactDate = dayjs();
	let toTsDayjs: dayjs.Dayjs;

	if (resolution === 'D') {
		toTsDayjs = currentExactDate
			.utc()
			.add(1, 'day')
			.set('hour', 0)
			.set('minute', 0);
	} else {
		toTsDayjs = currentExactDate.add(1, 'minute');
	}

	const toTs = toTsDayjs.set('millisecond', 0).valueOf();

	let fromTs = 0;

	if (resolution === '1') {
		fromTs = toTs - countback * ONE_MINUTE_MS;
	} else if (resolution === '5') {
		fromTs = toTs - countback * 5 * ONE_MINUTE_MS;
	} else if (resolution === '15') {
		fromTs = toTs - countback * 15 * ONE_MINUTE_MS;
	} else if (resolution === '60') {
		fromTs = toTs - countback * ONE_HOUR_MS;
	} else if (resolution === '240') {
		fromTs = toTs - countback * 4 * ONE_HOUR_MS;
	} else if (resolution === 'D') {
		fromTs = toTs - countback * ONE_DAY_MS;
	}

	return {
		fromTs,
		toTs,
	};
};

function normalizeTvResolution(
	interval: ResolutionString
): CandlesResolutionFromLocalStorage {
	const lower = String(interval).toLowerCase();
	if (/\dh$/.test(lower)) {
		const hours = parseInt(lower, 10);
		return String(hours * 60) as CandlesResolutionFromLocalStorage; // '4h' -> '240', '8h' -> '480'
	}
	return String(interval).toUpperCase() as CandlesResolutionFromLocalStorage;
}

const CandlesProvider = (props: PropsWithChildren<any>) => {
	const get = useVelocityStore((s) => s.get);
	const appEventEmitter = useAppEventEmitter();
	const pathname = usePathname();
	const getSelectedMarketId = () => get()?.selectedMarket?.marketId;
	const getSelectedMarketOraclePrice = () =>
		getLiveOracleData(getSelectedMarketId())?.price ?? ZERO_PRICE_BIGNUM;
	const selectedMarketId = useVelocityStore((s) => s.selectedMarket.marketId);

	const getCurrentMarketTradeHistory = () => get().marketTradeHistory;
	const [resolutionSetting] = useSyncLocalStorage<ResolutionString>(
		'chart_last_selected_interval',
		'60' as ResolutionString
	);

	// Check if current route needs candle data
	const needsCandles = pathname
		? needsDataRequirement(pathname, 'trading')
		: false;

	// Don't put a market filter in the buffer here, because we don't reinitialize the buffer when we change markets. Filtering needs to happen internally.
	const fillsBuffer = useFillEventsBuffer({
		customFilter: Candle.filterOrderActionsForCandles,
	});

	const [candleClient, setCandleClient] = useState<
		UICandleClient | undefined
	>();

	const marketIdRef = useRef(selectedMarketId);

	useEffect(() => {
		marketIdRef.current = selectedMarketId;
	}, [selectedMarketId]);

	/**
	 * Optimistically fetch candles for the initially selected market and resolution.
	 * The benefit is that the candles are ready to be loaded when the TV chart is ready to display them,
	 * hence saving the round trip cost of fetching the candles when the TV chart requests them for the first time.
	 */
	useEffect(() => {
		// Only fetch candles if current route needs them
		if (!needsCandles) {
			UICandleClient.hasLoadedFirstLoadedCandlesResolver?.();
			return;
		}

		const resolution = resolutionSetting; // 30 minutes is the default used by TradingView
		const normalizedResolution = normalizeTvResolution(resolution);
		const { resolution: candlesResolution, countback } =
			RESOLUTION_FETCH_DETAILS_MAP[normalizedResolution];
		const { fromTs, toTs } = getToAndFromTs(normalizedResolution);

		candleHistoryFetcher(
			candlesResolution,
			selectedMarketId,
			toTs,
			fromTs,
			countback
		)
			.then((result) => {
				if (
					selectedMarketId.equals &&
					!selectedMarketId.equals(marketIdRef.current)
				) {
					// the market has changed, so we don't store the candles
					return;
				}

				UICandleClient.setFirstLoadedCandles({
					result: result as {
						candles: UISerializableCandle[];
						lastTradeSeen: UISerializableOrderActionRecord;
						beyondMaxLookback?: boolean;
					},
					marketId: selectedMarketId,
					fetchedAtTs: Date.now(),
					from: fromTs,
					to: toTs,
				});
			})
			.finally(() => {
				UICandleClient.hasLoadedFirstLoadedCandlesResolver?.();
			});
	}, [needsCandles]);

	useEffect(() => {
		if (!fillsBuffer || !needsCandles) {
			// Clear candle client if not needed
			if (!needsCandles && candleClient) {
				candleClient.clearAllSubscriptions();
				setCandleClient(undefined);
			}
			return undefined;
		}

		let newOrCurrentCandleClient = candleClient;

		if (!newOrCurrentCandleClient) {
			newOrCurrentCandleClient = new UICandleClient(
				getCurrentMarketTradeHistory,
				fillsBuffer,
				getSelectedMarketOraclePrice,
				candleHistoryFetcher as any,
				appEventEmitter
			);
			// console.log('** NEW CANDLE CLIENT **');
			setCandleClient(newOrCurrentCandleClient);
		} else {
			// console.log('** NEW FILLS BUFFER, SAME CANDLE CLIENT');
			newOrCurrentCandleClient.setUnhandledMarketTradesBuffer(fillsBuffer);
		}
	}, [fillsBuffer, candleClient, needsCandles]);

	// Handle clearing all subscriptions if the candleClient changes
	useEffect(() => {
		if (!candleClient) {
			return;
		}

		return () => {
			candleClient.clearAllSubscriptions();
		};
	}, [candleClient]);

	return <CandlesContext value={candleClient}>{props.children}</CandlesContext>;
};

export default CandlesProvider;
